import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { AccountInfo } from "@azure/msal-browser";
import { Role } from "../constants/roles";
import { useMsal } from "@azure/msal-react";
import { resolveRoles } from "../services/auth/resolveRoles";

interface AuthContextType {
  identity: AccountInfo | null;
  roles: Role[];
  isAuthenticated: boolean | null;
  hasRole: (role: Role) => boolean;
  hasAnyRole: (roles: Role[]) => boolean;
  hasAllRoles: (roles: Role[]) => boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const normalizeRole = (role: string) => role.trim().toUpperCase();

interface AuthContextProviderProps {
  children: ReactNode;
}

export const AuthContextProvider = ({ children }: AuthContextProviderProps) => {
  const { instance } = useMsal();
  const [identity, setIdentity] = useState<AccountInfo | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    const account = instance?.getActiveAccount();

    if (account) {
      setIdentity(account);
      setIsAuthenticated(true);
      const finalRoles = resolveRoles(account);
      setRoles(finalRoles);
    }
  }, [instance]);

  const hasRole = (role: Role): boolean =>
    roles.some((currentRole) => normalizeRole(currentRole) === normalizeRole(role));

  const hasAnyRole = (rolesToCheck: Role[]): boolean =>
    rolesToCheck.some((role) => hasRole(role));

  const hasAllRoles = (rolesToCheck: Role[]): boolean =>
    rolesToCheck.every((role) => hasRole(role));

  const logout = () => {
    instance?.logoutRedirect();
  };

  return (
    <AuthContext.Provider
      value={{ identity, roles, isAuthenticated, hasRole, hasAnyRole, hasAllRoles, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthContextProvider");
  }

  return context;
};
